// HIV.gov Locator map page (/map/?location=30303&filter=hivtesting,pep).
// Same look as locator.hiv.gov/map; underneath it's this project's locator:
// the national sites archive read by byte range (instant, no API), a search
// radius chosen from local density, numbered pins that match the list and never
// renumber as you look around, every other matching place as a dot at any zoom,
// and pins decluttered so every number is readable.
//
// The results follow the map, and "Search this area" (shown after you move the
// map) re-searches and renumbers what's on screen whenever you want a fresh list:
// - Same area: you're looking around your search (its spot or most of its pins
//   are still on screen). Numbers stay; places coming into view are added, up to 99.
// - New area: the map settles where the search spot is off screen and its pins
//   are a small share of what's in view. The view is searched and renumbered.
// - Overview: zoomed out to many places over a wide area. No numbered pins, just
//   dots; the panel lists the areas in view with counts. Zooming in brings back
//   the search if it's there, or searches the new spot.
import { SiteData } from './data.js';
import { rank, autoRadius, miles, openStatus, spotGrouper, parseHours, RADIUS_STEPS } from './finder.js';
import { ResultsMap } from './map.js';
import { flagIs, FLAGS } from './flags.js';
import { CATEGORY, SHOWN_CATEGORIES, SHOWN_CATEGORY, GROUPS, hasCategory, anyCategoryExpr, categoriesFromIntent } from './categories.js';
import { parseLocation } from './place.js';
import { groupAreas } from './areas.js';
import { initTheme } from './hivgov-theme.js';
import { track, pageview, setCommon, bucket, zip3 } from './track.js';
import { registerServiceWorker } from './install.js';
registerServiceWorker();
import { initLanguage, t, getLang, categoryLabel, groupLabel, statusText, milesText } from './i18n.js';
import { at, pathTo } from './site.js';

const $ = (id) => document.getElementById(id);
const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
const NUMBERED = 99; // numbered pins and cards; more places show as dots
const PAGE = 50; // cards rendered at a time
const narrow = matchMedia('(max-width: 899px)');
const FORMAT_ERROR = () => t('formatError');
// Services on a card, grouped by condition the way HIV.gov's cards read.
const CARD_ORDER = ['condoms', 'hivtesting', 'hivprevention', 'hivtreatment', 'preptesting', 'pep', 'doxypep', 'selftesting',
  'mailintesting', 'ryanwhite', 'stdtesting', 'stiprevention', 'stitreatment', 'heptesting', 'hepprevention', 'hepc',
  'tbtesting', 'tbprevention', 'tbtreatment', 'clinics', 'substanceabuse', 'housing'];

initLanguage($('lang'));
initTheme(null);
setCommon({ p: 'map', lang: getLang() });
pageview('map');
const data = new SiteData();
const params = new URLSearchParams(location.search);
const state = {
  origin: null, label: '', kind: 'zip', // 'zip' | 'place' | 'gps' | 'area'
  // ?filter= names categories; a partner's ?intent= names a need (categories.js). filter wins.
  selected: new Set(params.has('filter') ? params.get('filter').split(',').filter((k) => SHOWN_CATEGORY[k]) : categoriesFromIntent(location.search) ?? []),
  es: params.get('es') === '1', // Spanish-speaking staff filter (lang= is the page language)
  radius: null, minRadius: 0, shown: PAGE, selectedId: null, fitNext: true,
  view: 'results', // 'results' | 'filters'
  overview: false,
  wide: false, // a wide view with few places: all listed and counted, none numbered
  // "Show places as": auto (the rules below), numbered (always numbers, nearest the
  // middle of the map), or dots (always the density view). In the link as show=.
  show: ['numbered', 'dots'].includes(params.get('show')) ? params.get('show') : 'auto',
  picks: [], // places picked by tapping a dot: rows ({ site, status, members }), lettered A-Z then bigger dots
  searchKind: 'zip', // how the current search started, for usage counts: zip | place | gps | area | link
};
let current = { rows: [], sites: [] };
// A shared link's view (at=lat,lon,zoom) and selected place (place=id), applied once.
const sharedAt = /^(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?),(\d+(?:\.\d+)?)$/.exec(params.get('at') || '');
let sharedAtPending = !!sharedAt; // don't overwrite a shared view before it's shown
let pendingPlace = /^\d+$/.test(params.get('place') || '') ? +params.get('place') : null;
let pendingArea = null;
let scanTimer = null;

const map = new ResultsMap($('map'), {
  onSelect: (id) => select(id, { from: 'map' }),
  onMoved: (center, radius) => {
    pendingArea = { center, radius };
    state.moved = true; // a person moved the map since the last search
    updateSearchArea();
    clearTimeout(scanTimer); scanTimer = setTimeout(whenSettled, 350);
  },
  // Tapping a place that isn't numbered promotes it to a pick (lettered pin + card).
  onDot: (props, latLon) => pick({ ...props, lat: latLon[0], lon: latLon[1] }),
  onFarDot: async (props, latLon) => {
    if (state.overview) { map.zoomInOn([latLon[1], latLon[0]]); return; } // nothing to pick in the overview
    const site = await data.siteAt(props.id, latLon[0], latLon[1]); // far-out dots carry only id and categories
    if (site) pick(site); else map.zoomInOn([latLon[1], latLon[0]]);
  },
  // A result's dot: in the wide view it's an unnumbered result (select its card); past 99, promote it.
  onExtraDot: (id) => { const row = current.rows.find((r) => r.site.id === id); if (!row) return; if (state.wide) select(id, { from: 'map' }); else pick(row.site); },
  onDataGone: () => data.refresh(),
}, {
  // Our basemap, pins and colors (clearer than HIV.gov's); only the zoom buttons move.
  navPosition: 'bottom-right',
  // HIV.gov opens on the whole result set, not just the nearest few.
  fitCount: NUMBERED,
  // This page decides when pins become dots (the overview), not a fixed zoom-out.
  autoDots: false,
  // Outside the overview every unnumbered place (past 99, or not in the results)
  // is a mid-size slate dot; tiny dots are only for the overview's density view.
  mediumExtraDots: true,
  // Keep pins clear of what's drawn over the map: the All Filters button, and on
  // a phone the search bar on top and the results sheet at the bottom.
  fitPadding: () => (narrow.matches ? { top: 150, bottom: 90, left: 30, right: 30 } : { top: 100, bottom: 50, left: 40, right: 60 }),
});
if (params.has('debug')) window.__locatorMap = map;
if (params.has('debug')) { window.__locatorPick = (site) => pick(site); window.__locatorData = data; }
// New data published while this page was open: follow it (data.js refresh()).
data.onArchiveChange = (archive) => map.setArchive(archive);

/* ---------- Search ---------- */
const now = () => { const d = new Date(); return [d.getDay(), d.getHours() * 60 + d.getMinutes()]; };
const matchFn = () => (state.selected.size ? (s) => [...state.selected].some((k) => hasCategory(s, k)) : null);
const searchOpts = () => ({ origin: state.origin, intentId: 'any', filterIds: state.es ? ['es'] : [], when: now(), match: matchFn() });

let searchSeq = 0;
async function search() {
  if (!state.origin) return;
  const seq = ++searchSeq;
  const res = await autoRadius(
    async (r) => (await data.near(state.origin[0], state.origin[1], r)).sites,
    searchOpts(),
    { minRadius: state.minRadius },
  );
  if (seq !== searchSeq) return;
  state.radius = res.radius;
  current = { rows: res.ranked.rows, sites: res.sites };
  state.wide = false;
  state.wideNumbered = false;
  state.moved = false;
  updateSearchArea();
  trackSearch(state.searchKind);
  if (map.loaded) map.closePopup();
  if (!state.fitNext) map.markFocus();
  render();
}

function researchFromNearby() {
  state.shown = PAGE; state.minRadius = 0; state.fitNext = true; state.selectedId = null;
  return search();
}

/** Services or language changed: never move the map; if it was moved, search what's on screen. */
/**
 * Services or language changed. Never move the map. Search exactly what's on
 * screen, so the results match the counts in All Filters (which are counts of
 * what's in view): no widening past the view. Before the map is up (or with no
 * search yet), fall back to a normal search.
 */
function refine() {
  state.shown = PAGE; state.minRadius = 0; state.fitNext = false;
  writeUrl();
  if (state.overview) {
    // Overview: the dots and area counts follow the filter. If it leaves few
    // enough places in view to number, show them as results right away.
    state.staleResults = true; // the old results list no longer matches the filters
    renderBadge();
    map.setBackgroundFilter(backgroundFilter());
    map.map.once('idle', () => {
      if (!state.overview) return;
      const radius = map.viewArea().radius;
      if (leavesOverview({ radius, total: placesInView() })) {
        state.overview = false; map.setOverview(false);
        if (radius > OVERVIEW_ENTER.miles) searchWide(); else searchInView();
      } else renderAreas();
    });
    updateCounts();
    return;
  }
  if (!map.loaded || !state.origin) { search(); return; }
  if (state.wide) {
    // Wide view: the filters decide between a list (few places) and the overview.
    const radius = map.viewArea().radius;
    viewPlaces().then((spots) => {
      if (wantsOverview({ radius, total: spots.filter(offersSelected).length })) enterOverview();
      else searchWide();
    });
    return;
  }
  searchInView();
}

/** show=numbered in a wide view: the 99 places nearest the middle of the map, numbered. */
function searchNearestInView() {
  state.wide = false;
  state.wideNumbered = true;
  state.searchKind = 'area';
  adoptArea(map.viewArea());
  pendingArea = null;
  state.shown = PAGE; state.fitNext = false;
  writeUrl();
  return searchInView({ nearest: NUMBERED });
}

/** Number everything in view, with distances from the view's center. */
function searchInViewFromHere() {
  state.wideNumbered = false;
  state.wide = true;
  state.searchKind = 'area';
  adoptArea(map.viewArea());
  pendingArea = null;
  state.shown = PAGE; state.fitNext = false;
  writeUrl();
  return searchInView();
}

let viewSeq = 0;
async function searchInView({ nearest = 0 } = {}) {
  const seq = ++viewSeq;
  const v = map.viewArea();
  const b = map.map.getBounds();
  // Looked somewhere else since the search: distances are from here now.
  if (!b.contains([state.origin[1], state.origin[0]])) adoptArea({ center: v.center, radius: v.radius });
  // Every place in view, with full records: directly when zoomed in, else from
  // the detailed tiles of just the places the slim far-out dots show.
  const { sites: found, layer } = await data.inBounds({ west: b.getWest(), south: b.getSouth(), east: b.getEast(), north: b.getNorth() });
  // nearest: only the closest matching places need full records (a wide view can hold thousands).
  const d = (s) => (s.lat - v.center[0]) ** 2 + ((s.lon - v.center[1]) * Math.cos((v.center[0] * Math.PI) / 180)) ** 2;
  const candidates = nearest && layer !== 'sites'
    ? found.filter((s) => (!state.es || isSpanish(s)) && (!state.selected.size || [...state.selected].some((k) => hasCategory(s, k)))).sort((a, b) => d(a) - d(b)).slice(0, nearest * 2)
    : found;
  const sites = layer === 'sites' ? found : await data.sitesFor(candidates);
  if (seq !== viewSeq) return;
  const { rows } = rank(sites, { ...searchOpts(), origin: v.center, radius: Infinity });
  state.radius = v.radius;
  state.staleResults = false;
  current = { rows: rows.filter((r) => b.contains([r.site.lon, r.site.lat])), sites };
  state.moved = false;
  updateSearchArea();
  trackSearch('filter');
  map.closePopup();
  map.markFocus();
  render();
}

function adoptArea(area) {
  state.origin = area.center;
  state.minRadius = Math.min(100, Math.max(1, Math.round(area.radius * 10) / 10));
  state.label = 'this map area';
  state.kind = 'area';
  $('location').value = '';
  $('location').placeholder = t('mapArea');
}

/* ---------- Picks ---------- */
// A tapped dot becomes a pick: letters A-Z, then a bigger dot. Picks stay
// through new searches until cleared; one that a new search numbers (in the
// first 99) is just a numbered result again.
const pickLabel = (i) => (i < 26 ? String.fromCharCode(65 + i) : '');
const pickedIds = () => new Set(state.picks.map((p) => p.site.id));

function syncPicks() {
  const numbered = new Set(current.rows.slice(0, NUMBERED).map((r) => r.site.id));
  state.picks = state.picks.filter((p) => !numbered.has(p.site.id));
}

function pick(site) {
  if (!state.picks.some((p) => p.site.id === site.id)) {
    const [d, m] = now();
    const status = openStatus(parseHours(site.h), d, m);
    state.picks.push({ site, status, members: [{ site, status }] });
    track({ e: 'action', a: 'pick' });
    render();
  }
  select(site.id, { from: 'map' });
}

/* ---------- Rendering ---------- */
// Listings come from CDC NPIN; organizations add or correct theirs there.
const ADD_LISTING = `<p class="add-listing">${t('addListing')}</p><p class="add-listing about-link"><a href="${pathTo('about/')}${getLang() === 'es' ? '?lang=es' : ''}">${t('aboutLink')}</a></p>`;
function renderBadge() {
  const active = state.selected.size + (state.es ? 1 : 0);
  $('filter-badge').hidden = !active;
  $('filter-badge').textContent = String(active);
}

function render() {
  renderBadge(); // in the overview too: a filter is just as active there
  if (state.overview) { renderAreas(); return; }
  const n = current.rows.length;
  $('count').textContent = !state.origin ? t('enterLocation') : t(narrow.matches ? 'showingShort' : 'showing', { n });
  syncPicks();
  if (state.selectedId && !current.rows.some((r) => r.site.id === state.selectedId) && !state.picks.some((p) => p.site.id === state.selectedId)) state.selectedId = null;
  const picksHtml = state.picks.length ? `<div class="picks"><p class="found-divider picks-head"><span>${t('picked')}</span>
      <button type="button" id="clear-picks">${t('clearPicks')}</button></p>${state.picks.map((p, i) => card(p, pickLabel(i))).join('')}</div>` : '';

  if (state.origin && !n) {
    const farther = state.radius < RADIUS_STEPS[RADIUS_STEPS.length - 1];
    const filtered = state.selected.size || state.es;
    $('results').innerHTML = picksHtml + `<p class="empty">${t('noResults', { r: milesText(state.radius) })}${filtered ? t('tryRemoving') : ''}${farther ? t('zoomOutMore') : ''}</p>`;
  } else {
    const firstAdded = current.rows.findIndex((r) => r.added);
    $('results').innerHTML = picksHtml + current.rows.slice(0, state.shown).filter((r, i) => i < NUMBERED || !pickedIds().has(r.site.id)).map((r, i) =>
      (i === firstAdded ? `<p class="found-divider">${t('foundMoving')}</p>` : '') + card(r, state.wide ? NUMBERED + 1 : i + 1)).join('')
      + (n > state.shown ? `<button class="more" type="button" id="more">${t('showMore')}</button>` : '')
      + ADD_LISTING;
  }
  renderFilters();
  renderMap();
  updateCounts();
  if (pendingPlace != null && current.rows.some((r) => r.site.id === pendingPlace)) {
    const id = pendingPlace;
    pendingPlace = null;
    queueMicrotask(() => select(id, { from: 'map' }));
  }
}

function renderMap() {
  if (!map.loaded || !state.origin) return;
  map.selectedId = state.selectedId;
  map.setNumbered(state.wide ? 0 : NUMBERED);
  const fitted = state.fitNext;
  const picked = pickedIds();
  map.show({ origin: state.origin, rows: current.rows.filter((r, i) => i < NUMBERED || !picked.has(r.site.id)), label: '', kind: state.kind === 'gps' ? 'gps' : 'zip', fit: fitted });
  map.setPicks(state.picks.map((p, i) => ({ site: p.site, label: pickLabel(i) })));
  // No search spot in a wide view (it's the view's own center, not a place someone chose).
  if (map.originMarker) map.originMarker.getElement().style.visibility = state.wide || state.overview ? 'hidden' : '';
  map.setBackgroundFilter(backgroundFilter());
  state.fitNext = false;
  if (fitted && !sharedAtPending) writeUrl(); // the framed view is part of the link from the start
}

const fmtPhone = (p) => {
  const d = String(p || '').replace(/\D/g, '');
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : String(p || '');
};
const addressOf = (s) => `${s.a}, ${s.c}, ${s.st} ${s.z}`;
const directionsUrl = (s) => `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(addressOf(s))}&travelmode=driving`;

const SHARE_ICON = `<svg width="30" height="30" viewBox="0 0 36 36" fill="none" aria-hidden="true"><circle class="ring" cx="18" cy="18" r="17.5"/><g transform="translate(6 6)"><path stroke-linecap="round" stroke-linejoin="round" d="M7.217 10.907a2.25 2.25 0 1 0 0 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186 9.566-5.314m-9.566 7.5 9.566 5.314m0 0a2.25 2.25 0 1 0 3.935 2.186 2.25 2.25 0 0 0-3.935-2.186Zm0-12.814a2.25 2.25 0 1 0 3.933-2.185 2.25 2.25 0 0 0-3.933 2.185Z" stroke="#336BD4" stroke-width="1.5"/></g></svg>`;
const DIRECTIONS_ICON = `<svg width="30" height="30" viewBox="0 0 36 36" fill="none" aria-hidden="true"><circle class="ring" cx="18" cy="18" r="17.5"/><rect x="18.1924" y="9.70711" width="12" height="12" rx="1.5" transform="rotate(45 18.1924 9.70711)" fill="white" stroke="#336BD4"/><path fill-rule="evenodd" clip-rule="evenodd" d="M20 20.7735L23 17.8868L20 15V17.2201L16.3333 17.2201H15V18.5534V21.2201H16.3333V18.5534L20 18.5534V20.7735Z" fill="#336BD4"/></svg>`;

function card({ site: s, status: st, members = [{ site: s }] }, n) {
  const sites = members.map((m) => m.site);
  // With services chosen, what this place offers of them comes first and stands
  // out: it's why the place is in the results.
  const offered = CARD_ORDER.filter((k) => sites.some((x) => hasCategory(x, k)));
  const matched = offered.filter((k) => state.selected.has(k));
  const keys = [...matched, ...offered.filter((k) => !state.selected.has(k))];
  const others = [...new Set(sites.map((x) => x.n).filter((name) => name !== s.n))];
  const tel = String(s.ph || '').replace(/\D/g, '');
  const name = s.w ? `<a href="${esc(s.w)}" target="_blank" rel="noopener">${esc(s.n)}</a>` : `<span class="name">${esc(s.n)}</span>`;
  const num = typeof n === 'string' ? `<span class="card-num pick" aria-label="${esc(t('mapPin', { n: n || '•' }))}">${n || '•'}</span>`
    : n <= NUMBERED ? `<span class="card-num${st && st.open ? ' open' : ''}" data-digits="${String(n).length}" aria-label="${t('mapPin', { n })}">${n}</span>` : '';
  return `<article class="card${s.id === state.selectedId ? ' selected' : ''}" data-id="${s.id}">
    <div class="info-wrapper">
      <div class="card-info">
        <h2 class="card-title" tabindex="0">${num}${name}</h2>
        <a class="addr" href="https://maps.google.com/?q=${encodeURIComponent(addressOf(s))}" target="_blank" rel="noopener noreferrer">${esc(s.a)}<br>${esc(s.c)}, ${esc(s.st)} ${esc(s.z)}</a>
        ${tel ? `<a class="phone" href="tel:${tel}">${esc(fmtPhone(s.ph))}</a>` : ''}
        ${others.length ? `<div class="also">${t('alsoHere', { names: others.map(esc).join(', ') })}</div>` : ''}
      </div>
      <div class="map-directions">
        <button class="card-action" type="button" data-share="${s.id}">${SHARE_ICON}<span>${t('share')}</span></button>
        <a class="card-action" href="${directionsUrl(s)}" target="_blank" rel="noopener">${DIRECTIONS_ICON}<span>${t('directions')}</span></a>
      </div>
    </div>
    ${keys.length ? `<div class="type-wrapper">${keys.map((k) => `<div class="type${matched.includes(k) ? ' match' : ''}"><img class="svc-icon" src="${at(`hivgov/icons/${k}.svg`)}" alt="" width="28" height="28">${categoryLabel(CATEGORY[k])}</div>`).join('')}</div>` : ''}
    <hr>
    ${sites.some((x) => x.es) ? `<div class="language"><span class="hola" aria-hidden="true"></span>${t('spanishSpoken')}</div>` : '<div class="card-bottom"></div>'}
  </article>`;
}

/** All Filters: each service with how many places nearby offer it. */
const isSpanish = (s) => s.es === 1 || (s.f != null && Math.floor(s.f / 2 ** FLAGS.indexOf('es')) % 2 === 1);

/**
 * All Filters: for each service, how many places on screen offer it (with the
 * Spanish filter applied). Counted from the view, not from the results list,
 * so it matches what someone sees on the map; picking a service then shows
 * exactly that many (refine() searches the view).
 */
/**
 * The places on screen, one per spot ({ g: listings }), read from the view's
 * tiles (SiteData.inBounds), with the Spanish filter applied. The single source
 * for every "how many here" number: All Filters counts and the overview's list.
 */
async function viewPlaces() {
  const b = map.map.getBounds();
  const { sites } = await data.inBounds({ west: b.getWest(), south: b.getSouth(), east: b.getEast(), north: b.getNorth() });
  const spot = spotGrouper(), spots = [];
  for (const s of sites) {
    if (state.es && !isSpanish(s)) continue;
    const { group, isNew } = spot(s, () => ({ g: [] }));
    group.g.push(s);
    if (isNew) spots.push(group);
  }
  return spots;
}
const offersSelected = (p) => !state.selected.size || p.g.some((s) => [...state.selected].some((k) => hasCategory(s, k)));

let countSeq = 0;
async function viewCounts() {
  if (!map.loaded) return null;
  const seq = ++countSeq;
  const spots = await viewPlaces();
  if (seq !== countSeq) return null;
  const counts = {};
  for (const c of SHOWN_CATEGORIES) counts[c.key] = spots.filter((p) => p.g.some((s) => hasCategory(s, c.key))).length;
  counts.total = spots.filter(offersSelected).length;
  return counts;
}

function renderFilters(counts = lastCounts) {
  $('spanish').checked = state.es;
  if (counts) {
    $('filters-count').textContent = counts.total === 0 ? t('showingFiltersNone') : counts.total === 1 ? t('showingFiltersOne')
      : t('showingFilters', { n: counts.total.toLocaleString(getLang()) });
  }
  $('filter-groups').innerHTML = GROUPS.map((g) => `<div class="filter-category">
    <h3 class="filter-section-title">${groupLabel(g, 'panel')}</h3>
    <div class="filter-grid">${SHOWN_CATEGORIES.filter((c) => c.group === g.id).map((c) => `
      <button class="filter-chip" type="button" data-key="${c.key}" aria-pressed="${state.selected.has(c.key)}">
        <img class="svc-icon" src="${at(`hivgov/icons/${c.key}.svg`)}" alt="" width="31" height="31">
        <span class="chip-label">${categoryLabel(c, 'filters')}<br>(${counts ? counts[c.key].toLocaleString(getLang()) : '…'})</span>
      </button>`).join('')}
    </div></div>`).join('');
}
let lastCounts = null;
/** Recount for the current view (only while All Filters is open). */
async function updateCounts() {
  if (state.view !== 'filters') return;
  const counts = await viewCounts();
  if (counts) { lastCounts = counts; renderFilters(counts); }
}

function setView(view) {
  state.view = view;
  $('results-view').hidden = view !== 'results' && !narrow.matches;
  $('filters-view').hidden = view !== 'filters';
  document.querySelector('.page').classList.toggle('filtering', view === 'filters');
  $('all-filters').setAttribute('aria-expanded', String(view === 'filters'));
  if (view === 'filters') { renderFilters(); updateCounts(); }
}

function setSheet(open) {
  $('results-view').classList.toggle('expanded', open);
  $('count').setAttribute('aria-expanded', String(open));
}

function select(id, { from }) {
  state.selectedId = id;
  writeUrl();
  map.select(id, { pan: from !== 'map' });
  const i = current.rows.findIndex((r) => r.site.id === id);
  if (i >= state.shown) { state.shown = Math.ceil((i + 1) / PAGE) * PAGE; render(); }
  for (const el of document.querySelectorAll('#results .card')) el.classList.toggle('selected', +el.dataset.id === id);
  if (from === 'map') {
    if (state.view === 'filters') setView('results');
    if (narrow.matches) setSheet(true);
    const el = document.querySelector(`#results .card[data-id="${id}"]`);
    if (el) el.scrollIntoView({ block: 'start', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  }
}

/* ---------- Moving the map ---------- */
// The overview (tiny dots, list of areas) is for a wide view with lots of
// places: over ~50 mi (center to corner) and 200+ places. It ends under 35 mi,
// or at 150 places or fewer, when a wide view lists every place unnumbered
// (and a local one numbers them). The gaps keep it from flipping back and forth.
const OVERVIEW_ENTER = { miles: 50, places: 199 };
const OVERVIEW_LEAVE = { miles: 35, places: 150 };
// show=numbered still falls back to dots over 1,000 places in view (back under 800).
const NUMBERED_MAX = { enter: 1000, leave: 800 };
const wantsOverview = (v) => (state.show === 'auto' ? v.radius > OVERVIEW_ENTER.miles && v.total > OVERVIEW_ENTER.places
  : state.show === 'dots' || v.total > NUMBERED_MAX.enter);
const leavesOverview = (v) => (state.show === 'auto' ? v.radius < OVERVIEW_LEAVE.miles || v.total <= OVERVIEW_LEAVE.places
  : state.show === 'numbered' && v.total <= NUMBERED_MAX.leave);
/** A wide view's results: every place listed (auto), or the nearest 99 numbered (show=numbered). */
const searchWide = () => (state.show === 'numbered' ? searchNearestInView() : searchInViewFromHere());

function whenSettled() {
  const m = map.map;
  if (!m || m.isMoving()) { scanTimer = setTimeout(whenSettled, 200); return; }
  // Decide once the dots for this view are drawn (counts come from them).
  if (m.areTilesLoaded()) onSettled(); else m.once('idle', onSettled);
}

const listedIds = () => new Set([...current.rows, ...state.picks].flatMap((r) => r.members.map((m) => m.site.id)));

/** What's on screen: numbered places in view, every matching place in view, the view's size. */
function viewNow() {
  const bounds = map.map.getBounds();
  const numberedInView = current.rows.filter((r) => bounds.contains([r.site.lon, r.site.lat])).length;
  const listed = listedIds();
  const others = state.overview ? placesInView() : map.backgroundInView().filter((p) => !listed.has(p.id)).length;
  return { bounds, numberedInView, total: state.overview ? others : numberedInView + others, radius: pendingArea ? pendingArea.radius : 0 };
}

/** Share a link: the phone's share sheet where there is one, else copy it. */
async function shareLink({ title, text, url }, labelEl, label) {
  try {
    if (navigator.share && matchMedia('(pointer: coarse)').matches) { await navigator.share({ title, text, url }); return; }
  } catch { /* cancelled, or not allowed: fall back to copying */ }
  try { await navigator.clipboard.writeText(text ? `${text}\n${url}` : url); } catch { /* blocked: still confirm */ }
  labelEl.textContent = t('linkCopied');
  setTimeout(() => { labelEl.textContent = label; }, 2000);
}

let settleSeq = 0;
async function onSettled() {
  if (map.loaded && !sharedAtPending) writeUrl(); // the address bar follows the view
  updateCounts(); // the counts in All Filters are for what's on screen
  if (!map.loaded || !pendingArea) return;
  const seq = ++settleSeq;
  const v = viewNow();
  // Count places the same way the area list does (from the data, not from what's
  // drawn, which can still be the previous zoom's tiles right after a zoom).
  v.total = (await viewPlaces()).filter(offersSelected).length;
  if (seq !== settleSeq) return;
  if (!state.overview) {
    if (wantsOverview(v)) return enterOverview();
    // Wide but sparse: every place in view is a result, none numbered (no
    // center to be "nearest" to). Kept up to date as the map moves.
    if (state.origin && v.radius > OVERVIEW_ENTER.miles && (state.show === 'auto' || !state.wideNumbered)) return searchWide();
    // Zoomed back in from a wide view: numbers again, nearest the center first.
    if ((state.wide || state.wideNumbered) && v.radius < OVERVIEW_LEAVE.miles) { state.wide = false; state.wideNumbered = false; return searchView(); }
    if (state.origin && isNewArea(v)) return searchView();
    await scanArea(seq);
  } else if (leavesOverview(v)) {
    leaveOverview(v);
  } else {
    renderAreas();
  }
}

/** Looking somewhere else: the search spot is off screen and its pins are a small share of what's in view. */
function isNewArea(v) {
  const originInView = v.bounds.contains([state.origin[1], state.origin[0]]);
  return !originInView && v.numberedInView < 0.25 * v.total;
}

/** Usage count for a search: services, coarse area searched, results bucketed. */
function trackSearch(k) {
  let area;
  if (state.kind === 'zip' && /^\d{5}/.test(state.label)) area = zip3(state.label);
  else if (state.kind === 'place') area = parseLocation(state.label)?.state;
  track({ e: 'search', k, svc: [...state.selected].sort().join(','), area, n: bucket(current.rows.length) });
}

/** "Search this area": only after the map was moved, and not in the overview (nothing to number there). */
function updateSearchArea() {
  $('searcharea').hidden = !(state.moved && !state.overview && state.origin);
}

/** Search what the map shows and number it, without moving the map. */
function searchView() {
  state.searchKind = 'area';
  adoptArea(pendingArea);
  pendingArea = null;
  state.shown = PAGE; state.fitNext = false; state.selectedId = null;
  writeUrl();
  return search();
}

function enterOverview() {
  state.overview = true;
  updateSearchArea();
  // The filter panel and badge follow too (a filter change can bring us here).
  renderBadge();
  renderFilters();
  updateCounts();
  map.setOverview(true);
  map.closePopup();
  map.setBackgroundFilter(backgroundFilter());
  // The dot filter just changed: count once they're drawn.
  map.map.once('idle', () => { if (state.overview) renderAreas(); });
  renderAreas();
}

function leaveOverview(v) {
  state.overview = false;
  updateSearchArea();
  map.setOverview(false);
  // A wide view with few places: number all of them. Zoomed back in on the
  // search: bring it back. Anywhere else: search there.
  if ((v.radius ?? map.viewArea().radius) > OVERVIEW_ENTER.miles) { searchWide(); return; }
  const back = !state.staleResults && state.origin && v.bounds.contains([state.origin[1], state.origin[0]]);
  if (back) { render(); scanArea(++settleSeq); } else searchView();
}

const SCAN_TILES_WHILE_ROOM = 400;
/** Same area: matching places that come into view join the numbered set (up to 99). */
async function scanArea(seq) {
  if (!pendingArea || !map.loaded || !state.origin) return;
  const room = Math.max(0, NUMBERED - current.rows.length);
  const { center, radius } = pendingArea;
  if (!room || data.tileRange(center[0], center[1], radius).count > SCAN_TILES_WHILE_ROOM) return;
  const near = await data.near(center[0], center[1], radius);
  if (seq !== settleSeq || state.overview) return;
  const { rows } = rank(near.sites, { ...searchOpts(), origin: center, radius });
  const listed = listedIds();
  const bounds = map.map.getBounds();
  const toAdd = rows
    .filter((r) => !r.members.some((m) => listed.has(m.site.id)) && bounds.contains([r.site.lon, r.site.lat]))
    .slice(0, room)
    .map((r) => ({ ...r, added: true, dist: miles(state.origin[0], state.origin[1], r.site.lat, r.site.lon) }));
  if (toAdd.length) {
    current = { ...current, rows: [...current.rows, ...toAdd] };
    render();
  }
}


/** Overview panel: the areas in view, busiest first. States when the view is national, else cities. */
/** Places (one per spot, like everywhere else) among the dots in view. */
function spotsInView() {
  const spot = spotGrouper(), spots = [];
  for (const p of map.backgroundInView()) { const { isNew } = spot(p, () => ({})); if (isNew) spots.push(p); }
  return spots;
}
const placesInView = () => spotsInView().length;

let areasSeq = 0;
async function renderAreas() {
  const seq = ++areasSeq;
  const places = (await viewPlaces()).filter(offersSelected).map((p) => p.g[0]);
  if (seq !== areasSeq || !state.overview) return;
  // Self-correcting: if this view is local or quiet enough for numbers, show them.
  const view = map.viewArea();
  // Dot size from how spread out the dots are on screen (once they're drawn).
  if (map.map.areTilesLoaded()) map.updateDensity(); else map.map.once('idle', () => map.updateDensity());
  // Dots mode: the dots are tiny where it's dense (the Automatic rule), mid-size where it isn't.
  if (state.show === 'dots') {
    const dense = state.dense ? !(view.radius < OVERVIEW_LEAVE.miles || places.length <= OVERVIEW_LEAVE.places)
      : view.radius > OVERVIEW_ENTER.miles && places.length > OVERVIEW_ENTER.places;
    state.dense = dense;
    map.setDotSize(dense ? 'tiny' : 'medium');
  } else map.setDotSize(null);
  if (leavesOverview({ radius: view.radius, total: places.length })) {
    leaveOverview({ bounds: map.map.getBounds() });
    return;
  }
  const byState = (pendingArea ? pendingArea.radius : 0) > 400; // states only for a near-national view
  const list = groupAreas(places, byState);
  const n = places.length.toLocaleString(getLang());
  $('count').textContent = t(narrow.matches ? 'inViewShort' : 'inView', { n });
  $('results').innerHTML = `<p class="areas-hint">${t(state.show === 'numbered' ? 'tooManyToNumber' : 'areasHint')}</p>`
    + list.slice(0, 60).map((g) => `<button class="area" type="button" data-bounds="${g.w},${g.s},${g.e},${g.nn}">
        <span class="area-name">${esc(g.name)}</span><span class="area-count">${g.n === 1 ? t('place') : t('places', { n: g.n.toLocaleString(getLang()) })}</span></button>`).join('')
    + (list.length > 60 ? `<p class="areas-hint">${t('moreAreas', { n: list.length - 60 })}</p>` : '')
    + ADD_LISTING;
}

/** Dots for every other matching place: these services, this language, not already numbered. */
function backgroundFilter() {
  const services = anyCategoryExpr([...state.selected]);
  const listed = ['!', ['in', ['get', 'id'], ['literal', [...listedIds()]]]];
  // Overview: every matching place is a dot. Otherwise the numbered ones are pins.
  const near = state.overview ? ['all', services] : ['all', services, listed];
  const far = state.overview ? ['all', services] : ['all', services, listed];
  if (state.es) { near.push(['==', ['get', 'es'], 1]); far.push(flagIs('es')); }
  return { near, far };
}


/* ---------- Location ---------- */
/**
 * The link for what's on screen: the search (location or near), services,
 * language, the map view (at=lat,lon,zoom) and the selected place. The address
 * bar always holds it (replaceState: no history entry per pan), so it can be
 * copied or shared as is. `place` overrides the selected place.
 */
function permalink({ place = state.selectedId, share = false } = {}) {
  const q = new URLSearchParams();
  if (state.origin && (state.kind === 'gps' || state.kind === 'area')) q.set('near', state.origin.map((v) => v.toFixed(4)).join(','));
  else if (state.label) q.set('location', state.label);
  if (state.selected.size) q.set('filter', [...state.selected].join(','));
  if (state.es) q.set('es', '1');
  if (state.show !== 'auto') q.set('show', state.show);
  if (getLang() === 'es') q.set('lang', 'es');
  if (map.loaded && !sharedAtPending) {
    const v = map.viewArea();
    q.set('at', `${v.center[0].toFixed(4)},${v.center[1].toFixed(4)},${v.zoom.toFixed(1)}`);
  }
  if (place != null) q.set('place', String(place));
  if (params.has('debug') && !share) q.set('debug', ''); // stays on through reloads (language switch), never in shared links
  return pathTo(`map/?${q}`).replace(/%2C/g, ',');
}
function writeUrl() { history.replaceState(null, '', permalink()); }

function showError(html) {
  $('err').innerHTML = html;
  $('err').hidden = !html;
}

/** "30303" or "Atlanta, GA" -> searched; returns false if it can't be found. */
async function goLocation(text) {
  const loc = parseLocation(text);
  const pt = loc && (loc.zip ? await data.zip(loc.zip) : await data.place(loc.city, loc.state));
  if (!pt) { showError(FORMAT_ERROR()); return false; }
  showError('');
  state.origin = pt; state.label = text.trim(); state.kind = loc.zip ? 'zip' : 'place';
  if (state.searchKind !== 'link') state.searchKind = state.kind;
  $('location').value = state.label;
  $('location').placeholder = t('locationPlaceholder');
  // A page per place for search engines and tabs: "... near Atlanta, GA | Locator".
  document.title = t('titleNear', { place: state.label });
  pendingArea = null;
  leaveWideViews();
  writeUrl();
  await researchFromNearby(); // frames the map on the new place
  return true;
}

/**
 * A new place was entered: whatever the map was showing (overview, a wide
 * list), go back to numbered results around it, framed on it.
 */
function leaveWideViews() {
  if (state.overview) { state.overview = false; if (map.loaded) map.setOverview(false); }
  state.wide = false; state.wideNumbered = false; state.staleResults = false; state.moved = false;
  updateSearchArea();
}

/* ---------- Events ---------- */
$('search').addEventListener('submit', (e) => { e.preventDefault(); goLocation($('location').value); });
$('location').addEventListener('input', () => showError(''));
$('all-filters').addEventListener('click', () => {
  // With the side panel hidden, the filters open it (they live in it).
  if (document.querySelector('.page').classList.contains('collapsed')) { setCollapsed(false); setView('filters'); return; }
  setView(state.view === 'filters' ? 'results' : 'filters');
});
$('filters-done').addEventListener('click', () => setView('results'));
// Hide or show the side panel (wide screens), remembered for the visit.
function setCollapsed(on) {
  document.querySelector('.page').classList.toggle('collapsed', on);
  $('collapse').setAttribute('aria-expanded', String(!on));
  $('expand').hidden = !on;
  try { sessionStorage.setItem('collapsed', on ? '1' : ''); } catch { /* private mode */ }
  const moved = state.moved;
  map.resize();
  // A resize isn't the person moving the map: don't offer "Search this area" for it.
  setTimeout(() => { state.moved = moved; updateSearchArea(); }, 0);
}
$('collapse').addEventListener('click', () => setCollapsed(true));
$('expand').addEventListener('click', () => { setCollapsed(false); $('collapse').focus(); });
try { if (sessionStorage.getItem('collapsed') === '1' && !narrow.matches) queueMicrotask(() => setCollapsed(true)); } catch { /* private mode */ }
$('searcharea').addEventListener('click', () => {
  track({ e: 'action', a: 'search_area' });
  if (!pendingArea) pendingArea = map.viewArea();
  searchView();
});
$('share-view').addEventListener('click', () => {
  writeUrl();
  track({ e: 'action', a: 'share_view' });
  shareLink({ title: document.title, url: new URL(permalink({ share: true }) + '&via=share', location.href).href }, $('share-view').querySelector('span'), t('share'));
});
$('filters-back').addEventListener('click', () => setView('results'));
$('filters-reset').addEventListener('click', () => { state.selected.clear(); state.es = false; refine(); });
// "Show places as": force numbers or dots, or let the view decide.
function renderShowMode() {
  for (const b of document.querySelectorAll('#show-mode [data-show]')) b.setAttribute('aria-pressed', String(b.dataset.show === state.show));
}
$('show-mode').addEventListener('click', (e) => {
  const b = e.target.closest('[data-show]');
  if (!b || b.dataset.show === state.show) return;
  state.show = b.dataset.show;
  renderShowMode();
  writeUrl();
  if (state.show === 'dots') { if (!state.overview) enterOverview(); else renderAreas(); return; }
  map.setDotSize(null);
  if (state.show === 'numbered') {
    // Unreasonable to number (1,000+ in view): stay with dots, and say so.
    if (state.overview) { pendingArea = map.viewArea(); onSettled(); return; }
    if (map.viewArea().radius > OVERVIEW_ENTER.miles) searchNearestInView(); else searchView();
    return;
  }
  map.setDotSize(null);
  pendingArea = map.viewArea(); // auto: judge this view by the usual rules
  state.wideNumbered = false;
  onSettled();
});
renderShowMode();
$('spanish').addEventListener('change', (e) => { state.es = e.target.checked; track({ e: 'filter', f: 'es', on: state.es ? '1' : '0' }); refine(); });
$('filter-groups').addEventListener('click', (e) => {
  const b = e.target.closest('[data-key]');
  if (!b) return;
  const k = b.dataset.key;
  state.selected.has(k) ? state.selected.delete(k) : state.selected.add(k);
  track({ e: 'filter', f: k, on: state.selected.has(k) ? '1' : '0' });
  refine();
});
$('count').addEventListener('click', () => { if (narrow.matches) setSheet(!$('results-view').classList.contains('expanded')); });
$('results').addEventListener('click', async (e) => {
  if (e.target.closest('#more')) { state.shown += PAGE; render(); return; }
  if (e.target.closest('#clear-picks')) { state.picks = []; render(); return; }
  const area = e.target.closest('.area');
  if (area) {
    track({ e: 'action', a: 'area' });
    // A person's move (not byApp): when it settles the overview ends and that area is searched.
    const [w, so, ea, no] = area.dataset.bounds.split(',').map(Number);
    map.map.fitBounds([[w, so], [ea, no]], { padding: 60, maxZoom: 12, duration: 600 });
    return;
  }
  const share = e.target.closest('[data-share]');
  if (share) {
    const row = current.rows.find((r) => r.site.id === +share.dataset.share);
    const s = row.site;
    const url = new URL(permalink({ place: s.id, share: true }) + '&via=share', location.href).href;
    track({ e: 'action', a: 'share_place' });
    const text = `${s.n}\n${addressOf(s)}${s.ph ? `\n${fmtPhone(s.ph)}` : ''}`;
    await shareLink({ title: s.n, text, url }, share.querySelector('span'), t('share'));
    return;
  }
  if (e.target.closest('a, button')) return;
  const el = e.target.closest('.card');
  if (el) select(+el.dataset.id, { from: 'list' });
});
narrow.addEventListener('change', () => { if (narrow.matches && document.querySelector('.page').classList.contains('collapsed')) setCollapsed(false); setView(state.view); render(); map.resize(); });

// Usage counts for what people do with a place (never which place).
document.addEventListener('click', (e) => {
  const a = e.target.closest('a');
  if (!a) return;
  if (a.closest('.add-listing')) track({ e: 'action', a: 'add_listing' });
  else if (a.href.startsWith('tel:')) track({ e: 'action', a: 'call' });
  else if (a.href.includes('google.com/maps/dir/')) track({ e: 'action', a: 'directions' });
  else if (a.closest('.card-title, .pop-actions')) track({ e: 'action', a: 'website' });
});
$('lang').addEventListener('click', () => track({ e: 'action', a: 'lang' }));

/* ---------- Start ---------- */
setView('results');
renderFilters();
data.open()
  .then(async () => {
    const opened = map.open(data.archive).catch((err) => { $('maperr').textContent = err.message; });
    const near = /^(-?\d+(\.\d+)?),(-?\d+(\.\d+)?)$/.exec(params.get('near') || '');
    if (near) {
      state.origin = [+near[1], +near[3]]; state.kind = 'gps'; state.label = '';
      state.searchKind = sharedAt ? 'link' : 'gps';
      $('location').placeholder = t('yourLocation');
      await researchFromNearby(); // done before a shared view is applied, so it can't overwrite it
    } else if (params.get('location')) {
      if (sharedAt) state.searchKind = 'link';
      $('location').value = params.get('location');
      await goLocation(params.get('location'));
    } else {
      render();
    }
    await opened;
    map.resize();
    if (sharedAt) {
      // A shared link: show its exact view. Then judge it like any move (overview,
      // new area or same area) and keep the address bar in step from here on.
      state.fitNext = false;
      renderMap();
      map.jumpTo([+sharedAt[1], +sharedAt[2]], +sharedAt[3]);
      sharedAtPending = false;
      pendingArea = map.viewArea();
      whenSettled();
    } else {
      state.fitNext = true;
      renderMap();
    }
  })
  .catch((err) => {
    const msg = String((err && err.message) || err);
    $('count').textContent = /being updated/.test(msg) ? t('dataUpdating') : msg;
  });
